import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components';
import { Header } from '../landing/components/Header';
import { Footer } from '../landing/components/Footer';
import { buildWhatsAppUrl } from '@/constants/whatsapp';

const PROPOSTA_MESSAGE = 'Oi, tenho interesse na intervenção NR-1 da Plumaa';

const TIERS = [
	{ max: 10, price: 7.5, label: 'Micro', range: 'até 10', min: 40 },
	{ max: 30, price: 6, label: 'Pequeno', range: '11 – 30', min: 0 },
	{ max: 100, price: 4, label: 'Médio', range: '31 – 100', min: 0 },
	{
		max: Number.POSITIVE_INFINITY,
		price: 2.5,
		label: 'Grande',
		range: '100+',
		min: 0,
	},
];

const flowSteps = [
	{
		frequency: 'Onboarding',
		title: 'Ativação e configuração da empresa',
		description:
			'Sua empresa é cadastrada na plataforma. Definimos os grupos, a periodicidade dos envios e personalizamos os formulários de acordo com o perfil da equipe. Nenhum colaborador precisa agir manualmente para receber o acesso — o convite é enviado de forma automática.',
		chip: 'Ativação imediata após confirmação',
	},
	{
		frequency: 'Todo mês · automático',
		title: 'Formulários de bem-estar para cada colaborador',
		description:
			'A plataforma envia automaticamente os formulários de bem-estar para toda a equipe todo mês — sem depender do RH para disparar. Os instrumentos são curtos, validados cientificamente e medem engajamento, carga emocional, relações de trabalho e risco psicossocial.',
		chip: 'Disparos automáticos · sem ação do RH',
	},
	{
		frequency: 'Periodicamente · rodadas estratégicas',
		title: 'Aplicação do COPSOQ',
		description:
			'O COPSOQ (Copenhagen Psychosocial Questionnaire) é o instrumento mais reconhecido internacionalmente para mapeamento de riscos psicossociais — validado em 40+ países e aceito pelo Ministério do Trabalho. Não aplicamos todo mês: fazemos rodadas periódicas e estratégicas para garantir dados de qualidade, sem fatiga de resposta dos colaboradores.',
		chip: 'Instrumento validado · aceito pelo MTE',
	},
	{
		frequency: 'Todo mês · personalizado por dados',
		title: '2 intervenções com especialistas',
		description:
			'Com base nos dados coletados, agendamos 2 intervenções mensais com psicólogos e especialistas em saúde ocupacional. Cada encontro é direcionado pelos pontos críticos que os formulários identificaram — não são palestras genéricas. Tudo documentado e rastreado para auditoria.',
		chip: '2 intervenções/mês · rastreadas para NR-1',
	},
	{
		frequency: 'Todo mês · pronto para auditoria',
		title: 'RPBE + IPBE entregues ao RH',
		description:
			'Ao final de cada ciclo, o RH recebe o Relatório Plumaa de Bem-Estar (RPBE) com os indicadores da equipe: engajamento, evolução dos riscos e alertas psicossociais. Junto a ele, o IPBE — nosso índice proprietário de bem-estar, aprimorado com i.a. — sintetiza tudo em um número rastreável. Pronto para apresentar à fiscalização.',
		chip: 'RPBE + IPBE mensais · válidos para auditoria',
	},
];

const deliverables = [
	{
		title: 'Formulários mensais de bem-estar',
		description:
			'Disparados automaticamente todo mês. Medem engajamento, saúde emocional e risco psicossocial — sem trabalho extra do RH.',
	},
	{
		title: 'COPSOQ periódico',
		description:
			'Instrumento internacional validado em 40+ países, aceito pelo MTE. Aplicado em rodadas estratégicas para mapeamento aprofundado.',
	},
	{
		title: '2 intervenções/mês',
		description:
			'Encontros com especialistas definidos pelos dados. Cada sessão rastreada e documentada — não são atividades genéricas de RH.',
	},
	{
		title: 'Plataforma individual',
		description:
			'Cada colaborador acessa psicólogos, nutricionista e programa de hábitos. Planos a partir de R$9.97 para o colaborador — o colaborador escolhe.',
	},
	{
		title: 'Relatório RPBE mensal',
		description:
			'Relatório com todos os indicadores da equipe entregue ao RH todo mês. Pronto para apresentar à fiscalização do MTE.',
	},
	{
		title: 'IPBE — Índice de Bem-Estar',
		description:
			'Número rastreável que sintetiza todos os dados da equipe. Atualizado mensalmente, aprimorado com i.a., válido para auditoria.',
	},
];

const planRows = [
	[
		'App de academia, dietas e rede social. (Fitfolio)',
		'—',
		'—',
		'—',
		'Incluído',
	],
	['Desconto em terapia online na Plumaa', '60%', '60%', '60%', '60%'],
	['Desconto em médico online na Plumaa', '70%', '70%', '70%', '70%'],
	['Desconto em nutricionista online na Plumaa', '60%', '60%', '60%', '60%'],
	['Rodas de conversa / palestras', '2', '2', '2', '2'],
	['Psicólogo online (sessões/mês)', '0', '1', '2', '4'],
	[
		'Valor para o colaborador / mês',
		'R$ 9,97',
		'R$ 49,97',
		'R$ 169,97',
		'R$ 197,99',
	],
];

const getTier = (collaborators: number) => {
	const tier =
		TIERS.find((item) => collaborators <= item.max) ??
		TIERS[TIERS.length - 1];
	return tier;
};

const toBrl = (value: number, decimals = false) => {
	if (decimals) return `R$ ${value.toFixed(2).replace('.', ',')}`;
	return `R$ ${Math.round(value).toLocaleString('pt-BR')}`;
};

export const PropostaPublica = () => {
	const [collaborators, setCollaborators] = useState(20);
	const whatsappUrl = useMemo(() => buildWhatsAppUrl(PROPOSTA_MESSAGE), []);

	const tier = useMemo(() => getTier(collaborators), [collaborators]);
	const monthly = useMemo(
		() => Math.max(collaborators * tier.price, tier.min || 0),
		[collaborators, tier],
	);
	const annual = monthly * 12;

	const handleCollaboratorsChange = (value: number) => {
		if (Number.isNaN(value)) return;
		setCollaborators(Math.max(1, Math.min(6000, value)));
	};

	return (
		<section className="flex w-full flex-col items-center bg-white font-sans text-gray-900">
			<Helmet>
				<title>Proposta Pública - Plumaa</title>
				<meta
					name="description"
					content="Proposta pública da Plumaa para adequação à NR-1 com monitoramento psicossocial e indicadores rastreáveis."
				/>
			</Helmet>

			<Header />

			<main className="w-full">
				<section className="relative flex min-h-[650px] w-full items-center overflow-hidden bg-zinc-800 px-6 py-16 md:h-[90vh] md:px-16 lg:px-24">
					<div className="absolute inset-0">
						<div className="absolute -top-20 left-[8%] size-72 rounded-full bg-brand-pink/55" />
						<div className="absolute bottom-10 right-[12%] size-80 rounded-full bg-brand-purple/45" />
						<div className="absolute right-1/3 top-1/3 size-56 rounded-full bg-brand-pink/45" />
					</div>
					<div className="absolute inset-0 bg-zinc-800/40 backdrop-blur-3xl" />
					<div className="absolute inset-0 bg-gradient-to-b from-zinc-800/45 via-zinc-800/30 to-zinc-900/50" />

					<div className="relative mx-auto w-full max-w-5xl space-y-12">
						<div className="flex flex-wrap items-center justify-between gap-4">
							<span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-pink">
								Plano Plumaa de Bem-Estar — PPBE
							</span>
							<span className="rounded-full border border-brand-pink/30 bg-brand-pink/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-brand-pink">
								Proposta Exclusiva · Abril 2026
							</span>
						</div>

						<div className="space-y-6">
							<h1 className="text-4xl font-black leading-[1.08] tracking-tight text-white md:text-6xl">
								Saúde mental virou{' '}
								<span className="text-brand-pink">
									obrigação legal.
								</span>
								<br />E prova de eficácia também.
							</h1>
							<p className="max-w-3xl text-base leading-relaxed text-zinc-300 md:text-lg">
								A NR-1 mudou. Sua empresa tem até maio de 2026
								para demonstrar que as ações de saúde mental
								funcionam — com dados, periodicidade e base
								científica.
							</p>
							<div className="inline-flex items-center gap-2 rounded-xl border border-zinc-500/50 bg-zinc-800/40 px-4 py-3 text-sm text-zinc-300">
								<span className="font-bold text-brand-pink">
									26/05/2026
								</span>
								<span>
									— Fiscalização ativa. Sem comprovação de
									eficácia, há risco de autuação.
								</span>
							</div>
						</div>
					</div>
				</section>

				<section className="bg-white px-6 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-12">
						<div className="space-y-4">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-pink">
								O que mudou na legislação
							</h4>
							<h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-5xl">
								A NR-1 não pede mais{' '}
								<span className="text-brand-pink">
									intenção.
								</span>{' '}
								Pede resultado.
							</h2>
							<p className="max-w-3xl text-zinc-500">
								A atualização de 2025 transformou a gestão de
								riscos psicossociais de recomendação em
								obrigação rastreável. Entenda o que isso
								significa para a sua empresa.
							</p>
						</div>

						<div className="rounded-3xl border-l-4 border-brand-pink bg-zinc-950 p-8">
							<p className="mb-3 text-[10px] font-black uppercase tracking-[0.2em] text-brand-pink">
								NR-1 · Item 1.5.4.4.5.3 · Vigência 26/05/2026
							</p>
							<p className="text-sm leading-relaxed text-zinc-300">
								A norma exige que as empresas implementem
								medidas de prevenção a riscos psicossociais{' '}
								<span className="font-semibold text-brand-pink">
									e comprovem sua eficácia
								</span>{' '}
								com monitoramento contínuo, periodicidade
								definida e base científica. Não basta ter um
								documento — é preciso mostrar que as ações estão
								funcionando.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
							<div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-8">
								<h3 className="mb-3 text-lg font-bold text-zinc-900">
									O que a fiscalização vai verificar
								</h3>
								<p className="text-sm leading-relaxed text-zinc-600">
									Os auditores do Ministério do Trabalho
									avaliarão se a empresa possui
									<span className="font-semibold text-zinc-800">
										{' '}
										identificação e avaliação dos riscos
										psicossociais
									</span>
									, medidas de controle implementadas e,
									principalmente,
									<span className="font-semibold text-zinc-800">
										{' '}
										evidências de monitoramento periódico
									</span>{' '}
									com indicadores mensuráveis. Empresas sem
									essa rastreabilidade ficam expostas a multas
									que variam de
									<span className="font-semibold text-zinc-800">
										{' '}
										R$ 1.800 a R$ 6.700 por colaborador
									</span>{' '}
									por autuação — com possibilidade de
									reincidência.
								</p>
							</div>
							<div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-8">
								<h3 className="mb-3 text-lg font-bold text-zinc-900">
									Por que isso é diferente do que existia
									antes
								</h3>
								<p className="text-sm leading-relaxed text-zinc-600">
									Anteriormente, bastava ter um PPRA ou PCMSO
									com menção à saúde mental. Com a NR-1
									atualizada, o GRO (Gerenciamento de Riscos
									Ocupacionais) exige que os riscos
									psicossociais sejam tratados com o mesmo
									rigor que riscos físicos e químicos:
									<span className="font-semibold text-zinc-800">
										{' '}
										periodicidade documentada, instrumento
										de avaliação validado e plano de ação
										rastreável.
									</span>{' '}
									Relatório anual não é suficiente.
								</p>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
							{[
								{
									title: '"Temos o documento, mas sem dados reais"',
									description:
										'A maioria das empresas tem políticas de saúde mental no papel. A NR-1 exige evidências de que essas políticas estão sendo monitoradas e gerando resultado.',
								},
								{
									title: '"Não temos tempo para aplicar pesquisas"',
									description:
										'O RH já está sobrecarregado. Um programa de monitoramento que depende de iniciativa manual do RH raramente acontece com a frequência que a norma exige.',
								},
								{
									title: 'Absenteísmo e turnover sem causa rastreável',
									description:
										'Problemas de saúde mental custam caro — mas sem métricas, a empresa não sabe onde está o problema, nem como provar que está agindo.',
								},
								{
									title: 'Risco trabalhista crescente',
									description:
										'Processos por assédio, burnout e adoecimento psíquico aumentaram 65% nos últimos 3 anos. Sem documentação de monitoramento, a empresa não tem como se defender.',
								},
							].map((item) => (
								<div
									key={item.title}
									className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6"
								>
									<h3 className="mb-2 text-sm font-bold text-zinc-900">
										{item.title}
									</h3>
									<p className="text-sm leading-relaxed text-zinc-600">
										{item.description}
									</p>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="bg-brand-pink px-6 py-28 md:px-16 md:py-32 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-12">
						<div className="space-y-3 text-white">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-pink-100">
								O custo de ignorar
							</h4>
							<h2 className="text-3xl font-bold tracking-tight md:text-5xl">
								Os números que justificam a decisão.
							</h2>
						</div>
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
							{[
								[
									'4×',
									'de retorno sobre cada real investido em saúde mental (OMS, 2022)',
								],
								[
									'-32%',
									'redução média de absenteísmo com programas de bem-estar ativos',
								],
								[
									'-40%',
									'redução de turnover em empresas com monitoramento contínuo',
								],
								[
									'R$150k',
									'custo médio de um processo trabalhista relacionado à saúde mental',
								],
							].map(([value, text]) => (
								<div
									key={value}
									className="rounded-2xl bg-black/20 p-6"
								>
									<div className="mb-2 text-4xl font-black leading-none text-white">
										{value}
									</div>
									<p className="text-sm leading-relaxed text-pink-50/90">
										{text}
									</p>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="bg-zinc-50 px-6 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-12">
						<div className="space-y-4">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-pink">
								Como o PPBE funciona
							</h4>
							<h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-5xl">
								Um fluxo claro,{' '}
								<span className="text-brand-pink">
									do diagnóstico à entrega.
								</span>
							</h2>
							<p className="max-w-3xl text-zinc-500">
								Veja exatamente o que acontece depois que sua
								empresa ativa o PPBE — sem caixas-pretas, sem
								etapas que dependem de iniciativa do RH.
							</p>
						</div>

						<div className="space-y-8">
							{flowSteps.map((item, index) => (
								<div
									key={item.title}
									className="grid grid-cols-[56px_1fr] gap-x-6"
								>
									<div className="flex flex-col items-center">
										<div className="flex aspect-square h-12 min-h-12 w-12 min-w-12 shrink-0 items-center justify-center rounded-full bg-brand-pink text-sm font-black text-white">
											{String(index + 1).padStart(2, '0')}
										</div>
										{index < flowSteps.length - 1 && (
											<div className="mt-2 h-full w-px bg-gradient-to-b from-brand-pink to-brand-pink/10" />
										)}
									</div>
									<div className="pb-6">
										<p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-brand-pink">
											{item.frequency}
										</p>
										<h3 className="mb-2 text-xl font-bold text-zinc-900">
											{item.title}
										</h3>
										<p className="text-sm leading-relaxed text-zinc-600">
											{item.description}
										</p>
										<span className="mt-3 inline-flex rounded-lg border border-brand-pink/20 bg-brand-pink/10 px-3 py-1 text-xs font-semibold text-brand-pink">
											{item.chip}
										</span>
									</div>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="bg-white px-6 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-12">
						<div className="space-y-4">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-pink">
								O que está incluído
							</h4>
							<h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-5xl">
								Cada componente resolve{' '}
								<span className="text-brand-pink">
									um problema real.
								</span>
							</h2>
							<p className="max-w-3xl text-zinc-500">
								O PPBE não é um benefício genérico. Cada
								elemento foi desenhado para gerar
								rastreabilidade e eficácia comprovável perante a
								NR-1.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
							{deliverables.map((item) => (
								<div
									key={item.title}
									className="rounded-3xl border border-zinc-200 bg-zinc-50 p-6"
								>
									<h3 className="mb-2 text-base font-bold text-zinc-900">
										{item.title}
									</h3>
									<p className="text-sm leading-relaxed text-zinc-600">
										{item.description}
									</p>
								</div>
							))}
						</div>

						<div className="rounded-[2rem] bg-zinc-950 p-8 md:p-12">
							<p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-brand-pink">
								Novidade Plumaa 2026
							</p>
							<h3 className="mb-3 text-2xl font-bold text-white">
								IPBE — Índice Plumaa de Bem-Estar
							</h3>
							<p className="max-w-4xl text-sm leading-relaxed text-zinc-300">
								Um índice proprietário que sintetiza riscos
								psicossociais, engajamento, absenteísmo e saúde
								emocional em um único número rastreável —
								entregue todo mês ao RH. Desenvolvido sobre a
								base científica do COPSOQ II-Br e aprimorado com
								i.a.
							</p>
							<div className="mt-5 flex flex-wrap gap-2">
								{[
									'Atualizado mensalmente',
									'Base COPSOQ II-Br',
									'Aprimorado com i.a',
									'Válido para auditoria NR-1',
								].map((pill) => (
									<span
										key={pill}
										className="rounded-full border border-brand-pink/35 bg-brand-pink/15 px-3 py-1 text-xs font-semibold text-brand-pink"
									>
										{pill}
									</span>
								))}
							</div>
						</div>
					</div>
				</section>

				<section className="bg-zinc-50 px-6 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-12">
						<div className="space-y-4">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-pink">
								Benefícios para o colaborador
							</h4>
							<h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-5xl">
								A empresa ativa o programa.
								<span className="text-brand-pink">
									{' '}
									O colaborador escolhe o plano.
								</span>
							</h2>
							<p className="max-w-3xl text-zinc-500">
								Além do acompanhamento institucional, cada
								colaborador ganha acesso à plataforma individual
								e escolhe livremente o plano que se encaixa na
								sua rotina:
							</p>
						</div>

						<div className="overflow-x-auto rounded-3xl border border-zinc-200 bg-white">
							<table className="w-full min-w-[780px] border-collapse text-left">
								<thead>
									<tr className="border-b border-zinc-200 bg-zinc-50">
										<th className="px-4 py-4 text-xs font-black uppercase tracking-wider text-zinc-500">
											Benefício
										</th>
										{[
											'Lite',
											'Go',
											'Standard',
											'Premium',
										].map((column) => (
											<th
												key={column}
												className={`px-4 py-4 text-center text-xs font-black uppercase tracking-wider ${
													column === 'Standard'
														? 'text-brand-pink'
														: 'text-zinc-500'
												}`}
											>
												{column}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{planRows.map((row, index) => (
										<tr
											key={row[0]}
											className="border-b border-zinc-100 last:border-none"
										>
											<td
												className={`px-4 py-4 text-sm ${
													index ===
													planRows.length - 1
														? 'font-bold text-zinc-900'
														: 'text-zinc-600'
												}`}
											>
												{row[0]}
											</td>
											{row
												.slice(1)
												.map((cell, cellIndex) => (
													<td
														key={`${row[0]}-${cellIndex}`}
														className={`px-4 py-4 text-center text-sm ${
															cellIndex === 2
																? 'bg-brand-pink/5 font-semibold text-brand-pink'
																: 'text-zinc-600'
														}`}
													>
														{cell}
													</td>
												))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<p className="text-xs text-zinc-500">
							* Os planos individuais são opcionais e custeados
							pelo próprio colaborador — ou pela empresa, conforme
							política de benefícios adotada.
						</p>
					</div>
				</section>

				<section className="bg-white px-6 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-12">
						<div className="space-y-4">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-pink">
								Investimento
							</h4>
							<h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-5xl">
								Quanto custa proteger{' '}
								<span className="text-brand-pink">
									sua empresa?
								</span>
							</h2>
							<p className="max-w-3xl text-zinc-500">
								O valor que a empresa paga varia conforme o
								tamanho da equipe — com 50% de desconto
								permanente. Simule abaixo:
							</p>
						</div>

						<div className="rounded-[2rem] bg-zinc-950 p-8 md:p-10">
							<div className="mb-8 flex flex-wrap items-end justify-between gap-4">
								<span className="text-sm font-semibold text-zinc-300">
									Número de colaboradores
								</span>
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={() =>
											handleCollaboratorsChange(
												collaborators - 1,
											)
										}
										className="flex h-12 w-12 items-center justify-center rounded-xl border border-zinc-600 bg-zinc-900/80 text-xl font-black text-zinc-200 transition-colors hover:border-brand-pink hover:text-white"
										aria-label="Diminuir colaboradores"
									>
										-
									</button>
									<input
										type="number"
										value={collaborators}
										min={1}
										max={6000}
										onChange={(event) =>
											handleCollaboratorsChange(
												Number(event.target.value),
											)
										}
										className="h-12 w-32 rounded-xl border border-zinc-600 bg-zinc-900/80 px-3 text-center text-2xl font-black tracking-tight text-white outline-none transition-colors [appearance:textfield] focus:border-brand-pink md:text-3xl [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
									/>
									<button
										type="button"
										onClick={() =>
											handleCollaboratorsChange(
												collaborators + 1,
											)
										}
										className="flex h-12 w-12 items-center justify-center rounded-xl border border-zinc-600 bg-zinc-900/80 text-xl font-black text-zinc-200 transition-colors hover:border-brand-pink hover:text-white"
										aria-label="Aumentar colaboradores"
									>
										+
									</button>
								</div>
							</div>

							<input
								type="range"
								min={1}
								max={6000}
								value={collaborators}
								onChange={(event) =>
									handleCollaboratorsChange(
										Number(event.target.value),
									)
								}
								className="mb-8 w-full accent-brand-pink"
							/>

							<div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4">
								{TIERS.map((item) => (
									<div
										key={item.label}
										className={`rounded-2xl border px-4 py-3 ${
											item.label === tier.label
												? 'border-brand-pink bg-brand-pink/20'
												: 'border-zinc-700 bg-zinc-900'
										}`}
									>
										<div className="text-xs font-black uppercase tracking-wider text-zinc-300">
											{item.label}
										</div>
										<div className="text-lg font-bold text-white">
											{toBrl(item.price, true)}
										</div>
										<div className="text-xs text-zinc-400">
											{item.range}
										</div>
									</div>
								))}
							</div>

							<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
								<div className="rounded-2xl border border-zinc-700 bg-zinc-900 p-5">
									<p className="text-xs uppercase tracking-wider text-zinc-400">
										Valor por colaborador/mês
									</p>
									<p className="mt-2 text-2xl font-bold text-white">
										{toBrl(tier.price, true)}
									</p>
									<p className="mt-1 text-xs text-zinc-500">
										{tier.label} · {collaborators}{' '}
										colaboradores
									</p>
								</div>
								<div className="rounded-2xl border border-zinc-700 bg-zinc-900 p-5">
									<p className="text-xs uppercase tracking-wider text-zinc-400">
										Investimento mensal
									</p>
									<p className="mt-2 text-2xl font-bold text-brand-pink">
										{toBrl(monthly)}
									</p>
									<p className="mt-1 text-xs text-zinc-500">
										{collaborators} col ×{' '}
										{toBrl(tier.price, true)}/mês
									</p>
								</div>
								<div className="rounded-2xl border border-brand-pink/30 bg-brand-pink/10 p-5 md:col-span-2">
									<p className="text-xs uppercase tracking-wider text-zinc-300">
										Investimento anual estimado · com 50% de
										desconto
									</p>
									<p className="mt-2 text-3xl font-black text-brand-pink">
										{toBrl(annual)}
									</p>
									<p className="mt-1 text-xs text-zinc-400">
										Desconto permanente após os 3 primeiros
										meses
									</p>
								</div>
							</div>

							<div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
								<div className="rounded-xl border border-zinc-700 bg-zinc-900 p-4">
									<p className="text-xs font-semibold text-zinc-400">
										Mês 1
									</p>
									<p className="text-lg font-bold text-brand-pink">
										Grátis
									</p>
									<p className="text-xs text-zinc-500">
										empresa free
									</p>
								</div>
								<div className="rounded-xl border border-zinc-700 bg-zinc-900 p-4">
									<p className="text-xs font-semibold text-zinc-400">
										Meses 2 – 3
									</p>
									<p className="text-lg font-bold text-brand-pink">
										50% off
									</p>
									<p className="text-xs text-zinc-500">
										{toBrl(monthly)}/mês
									</p>
								</div>
								<div className="rounded-xl border border-zinc-700 bg-zinc-900 p-4">
									<p className="text-xs font-semibold text-zinc-400">
										Mês 4 em diante
									</p>
									<p className="text-lg font-bold text-brand-pink">
										50% off
									</p>
									<p className="text-xs text-zinc-500">
										{toBrl(monthly)}/mês · desconto fixo
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<section className="bg-zinc-50 px-6 py-24 md:px-16 lg:px-24">
					<div className="mx-auto flex max-w-5xl flex-col items-center space-y-6 text-center">
						<h2 className="text-4xl font-black tracking-tight text-zinc-900 md:text-6xl">
							Pronto para transformar conformidade em vantagem?
						</h2>
						<p className="max-w-2xl text-zinc-500">
							Entre em contato e ative o PPBE para a sua equipe.
						</p>
						<Button
							onClick={() => window.open(whatsappUrl, '_blank')}
							className="justify-center rounded-full border-2 border-zinc-900 bg-brand-pink px-10 py-4 text-xs font-black uppercase tracking-[0.2em] text-white transition-all hover:bg-brand-pink/90"
							text="Falar com a Plumaa"
						/>
						<p className="text-xs text-zinc-400">
							Abril 2026
							<br />
							Elaborada pela Plumaa · plumaa.com.br
						</p>
					</div>
				</section>
			</main>

			<Footer />
		</section>
	);
};
